import type { ICustomControlDiagnosticReportReadProps } from '../../interfaces/customControlDiagnosticReportReadProps';
import type { IBevindingen } from './TfcV1DiagnosticReportAddEdit';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { DiagnosticReportReadButtons } from '../../../../elements/carePathButtons/DiagnosticReportReadButtons';
import { FormDivider, FormParagraph, HeaderDivider } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { FundusscreeningPolicyAndUrgencyReadFields } from '../../TFO/v1/FundusscreeningPolicyAndUrgencyReadFields';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';

export function TfcV1DiagnosticReportRead(props: ICustomControlDiagnosticReportReadProps) {
    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.diagnosticReport && props.diagnosticReport.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    })
                }
                organizationName={props.diagnosticReport.performerOrganization?.name}
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.category_consultReport')}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <HorizontalFormGroupRead>
                    <FormLabel>Bevindingen</FormLabel>
                    <InputContainer>
                        {bevindingenOption('niet_te_beoordelen', 'Niet te beoordelen')}
                        {bevindingenOption('geen_afwijkingen', 'Geen afwijkingen')}
                        {bevindingenOption('exsudaten', 'Exsudaten')}
                        {bevindingenOption('bloedinkjes', 'Bloedinkjes en microaneurysma')}
                        {bevindingenOption('cotton_wool_spots', 'Cotton wool spots')}
                        {bevindingenOption('andersText', 'Anders, namelijk...')}
                    </InputContainer>
                </HorizontalFormGroupRead>

                <Icd10SelectionsRead
                    diagnosis={props.diagnosticReport.diagnosis}
                    diagnosisConclusion={props.diagnosticReport.customControlData?.conclusie_diagnose}
                    hideTitle
                    showWorkingDiagnosis
                    thesaurusTermDate={props.diagnosticReport.dateSent}
                />

                <FormDivider />

                <HeaderDivider text="Advies" />

                <FundusscreeningPolicyAndUrgencyReadFields
                    customControlData={props.diagnosticReport.customControlData}
                />

                {props.diagnosticReport.customControlData.bezoek_aan_specialist_nu_nodig && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Bezoek aan specialist nu nodig?</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>
                                {props.diagnosticReport.customControlData.bezoek_aan_specialist_nu_nodig}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.diagnosticReport.remark && (
                    <>
                        <FormDivider />
                        <HorizontalFormGroupRead>
                            <FormLabel>Toelichting</FormLabel>
                            <InputContainer>
                                <FormParagraph bold>{props.diagnosticReport.remark}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    </>
                )}
                <DiagnosticReportReadButtons {...props} />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideConsultReport')}
                    labelMore={translateKey('diagnosticReport.showConsultReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );

    function bevindingenOption<K extends keyof IBevindingen>(key: K, label: string) {
        const bevindingen = props.diagnosticReport.customControlData.bevindingen;
        const value = bevindingen[key];
        const keyOD = `${key}_od`;
        const keyOS = `${key}_os`;
        const keyAnders = `${key}_anders`;

        return (
            value && (
                <div className="row">
                    <div className="col-sm-10">
                        <FormParagraph
                            bold
                            key={keyAnders}
                        >
                            {key !== 'andersText' ? label : value}
                        </FormParagraph>
                    </div>
                    <div className="col-sm-4">
                        <FormParagraph
                            bold
                            key={keyOD}
                        >
                            {bevindingen[keyOD] ? 'OD' : null}
                        </FormParagraph>
                    </div>
                    <div className="col-sm-4">
                        <FormParagraph
                            bold
                            key={keyOS}
                        >
                            {bevindingen[keyOS] ? 'OS' : null}
                        </FormParagraph>
                    </div>
                </div>
            )
        );
    }
}
